import React, { useEffect, useState } from "react";
import api from "../Services/api";
import "./CalendarioPagamento.css";

interface CalendarioData {
	Id: number;
	Title: string;
	Mes: string;
	Ano: number;
	PagamentoAdiantamentoQuinzenal: string;
	PagamentoFolhaMensal: string;
	EnvioContracheque: string;
	Observacoes: string;
	Ativo: boolean;
	DataEfetivaDeEnvio: string;
}

interface CalendarioResponse {
	results: CalendarioData[];
}

const CalendarioPagamento: React.FC = () => {

	const anoAtual = new Date().getFullYear();

	const [dadosOriginais, setDadosOriginais] = useState<CalendarioData[]>([]);
	const [dadosFiltrados, setDadosFiltrados] = useState<CalendarioData[]>([]);
	const [anoSelecionado, setAnoSelecionado] = useState<number>(anoAtual);
	const [mesSelecionado, setMesSelecionado] = useState<string>("");
	const [isLoading, setIsLoading] = useState(true);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const carregarCalendario = async () => {
			try {
				setIsLoading(true);

				/* Voltar essa parte assim que a tabela estiver configurada no SharePoint */
				const response = await api.get<CalendarioResponse>("/ObterCalendarioPagamentoBeneficio");
				const dados = response.data.results;

				// Carregar mock diretamente
				// const response = await fetch('/mock-api/ObterCalendarioPagamentoBeneficio');
				// const data = await response.json();
				// const dados = data.d.results;

				setDadosOriginais(dados);
				setDadosFiltrados(dados);
			} catch (error) {
				console.error("Erro ao carregar calendário:", error);
				setDadosOriginais([]);
				setDadosFiltrados([]);
			} finally {
				setIsLoading(false);
			}
		};

		carregarCalendario();
	}, []);

	useEffect(() => {
		aplicarFiltros();
	}, [anoSelecionado, mesSelecionado, dadosOriginais]);

	const aplicarFiltros = () => {
		let dadosFiltrados = dadosOriginais.filter((item) => {
			const matchAno = !anoSelecionado || item.Ano === anoSelecionado;
			const matchMes = !mesSelecionado || item.Mes === mesSelecionado;
			return matchAno && matchMes && item.Ativo;
		});

		setDadosFiltrados(dadosFiltrados);
	};

	const meses = [
		"Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
		"Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
	];
	
	const anoInicial = 2024;

	function anosDisponiveis () {
		const anos: number[] = [];
		for (let ano = anoAtual; ano >= anoInicial; ano--) {
			anos.push(ano);
		}
		return anos;
	};

	const anos = anosDisponiveis();

	if (isLoading) {
		return (
			<div className="col-lg-10">
				<div className="loading-state text-center py-8">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Carregando...</span>
					</div>
					<p className="mt-3 text-muted">Carregando calendário...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="col-lg-10">
			{/* Filtros */}
			<div className="filtros-section mb-6">
				<div className="card">
					<div className="card-body">
						<h3 className="card-title mb-4">Filtro</h3>
						<div className="row">
							<div className="col-md-4 mb-3">
								<label htmlFor="ano-filter" className="form-label">Ano</label>
								<select
									id="ano-filter"
									value={anoSelecionado}
									onChange={(e) => setAnoSelecionado(Number(e.target.value))}
									className="form-select"
								>									
									{anos.map((ano) => (
										<option key={ano} value={ano}>
											{ano}
										</option>
									))}
								</select>
							</div>
							<div className="col-md-4 mb-3">
								<label htmlFor="mes-filter" className="form-label">Mês</label>
								<select
									id="mes-filter"
									value={mesSelecionado}
									onChange={(e) => setMesSelecionado(e.target.value)}
									className="form-select"
								>
									<option value="">Todos os meses</option>
									{meses.map((mes) => (
										<option key={mes} value={mes}>
											{mes}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Tabela/Cards */}
			<div className="calendario-section mb-6">
				<div className="card">
					<div className="card-header bg-primary text-white">
						<h3 className="card-title mb-0">
							{anoSelecionado ?
								`Calendário de Pagamentos - ${anoSelecionado}` :
								"Calendário de Pagamentos"
							}
						</h3>
					</div>
					<div className="card-body p-0">
						{dadosFiltrados.length === 0 ? (
							<div className="text-center py-8">
								<div className="mb-4">
									<i className="fas fa-calendar-alt fa-3x text-muted"></i>
								</div>
								<h4 className="text-muted">Nenhum registro encontrado</h4>
								<p className="text-muted">Não foram encontrados registros para os filtros selecionados.</p>
							</div>
						) : (
							<>
								{/* Tabela Desktop */}
								{!isMobile && (
									<div className="table-responsive">
										<table className="table table-hover mb-0">
											<thead className="table-light">
												<tr>
													<th scope="col">Mês</th>
													<th scope="col">Adiantamento Quinzenal</th>
													<th scope="col">Pagamento Folha Mensal</th>
													<th scope="col">Envio Contracheque</th>
													<th scope="col">Data Efetiva de Envio</th>
												</tr>
											</thead>
											<tbody>
												{dadosFiltrados.map((item) => (
													<tr key={item.Id}>
														<td className="col-mes">
															<strong>{item.Mes}</strong>
														</td>
														<td>
															<span className={`badge ${item.PagamentoAdiantamentoQuinzenal === '-' ? 'bg-secondary' : 'bg-primary'}`}>
																{item.PagamentoAdiantamentoQuinzenal}
															</span>
														</td>
														<td>
															<span className="badge bg-primary">
																{item.PagamentoFolhaMensal}
															</span>
														</td>
														<td>
															<span className="badge bg-primary">
																{item.EnvioContracheque}
															</span>
														</td>
														<td>
															<span className="badge bg-primary">
																{item.DataEfetivaDeEnvio}
															</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}

								{/* Cards Mobile */}
								{isMobile && (
									<div className="p-3">
										{dadosFiltrados.map((item) => (
											<div key={item.Id} className="card mb-3">
												<div className="card-body">
													<div className="row align-items-center mb-3">
														<div className="col-6">
															<h5 className="card-title mb-0">{item.Mes}</h5>
														</div>
													</div>
													<div className="row mb-2">
														<div className="col-8">
															<small className="text-muted">Pagamento Quinzenal:</small>
														</div>
														<div className="col-4 text-end">
															<span className={`badge ${item.PagamentoAdiantamentoQuinzenal === '-' ? 'bg-secondary' : 'bg-primary'}`}>
																{item.PagamentoAdiantamentoQuinzenal}
															</span>
														</div>
													</div>
													<div className="row mb-2">
														<div className="col-8">
															<small className="text-muted">Pagamento Mensal:</small>
														</div>
														<div className="col-4 text-end">
															<span className="badge bg-primary">
																{item.PagamentoFolhaMensal}
															</span>
														</div>
													</div>
													<div className="row mb-2">
														<div className="col-8">
															<small className="text-muted">Envio Contracheque:</small>
														</div>
														<div className="col-4 text-end">
															<span className="badge bg-primary">
																{item.EnvioContracheque}
															</span>
														</div>
													</div>
													{item.Observacoes && item.Observacoes !== "Pagamento realizado conforme cronograma" && (
														<div className="mt-2 pt-2 border-top">
															<small className="text-muted fst-italic">{item.Observacoes}</small>
														</div>
													)}
												</div>
											</div>
										))}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>

			{/* Informações Adicionais */}
			<div className="info-section">
				<div className="row">
					<div className="col-lg-6 mb-4">
						<div className="card h-100 border-primary">
							<div className="card-body">
								<h4 className="card-title text-primary">
									<i className="fas fa-info-circle me-2"></i>
									Informações Importantes
								</h4>
								<ul className="list-unstyled">
									<li className="mb-2">
										<i className="fas fa-check text-success me-2"></i>
										Os pagamentos são realizados nas datas especificadas no cronograma
									</li>
									<li className="mb-2">
										<i className="fas fa-check text-success me-2"></i>
										O contracheque é enviado antes da data de pagamento
									</li>
									<li className="mb-2">
										<i className="fas fa-check text-success me-2"></i>
										Em dezembro não há pagamento quinzenal
									</li>
								</ul>
							</div>
						</div>
					</div>
					<div className="col-lg-6 mb-4">
						<div className="card h-100 border-warning">
							<div className="card-body">
								<h4 className="card-title text-warning">
									<i className="fas fa-exclamation-triangle me-2"></i>
									Dúvidas?
								</h4>
								<p className="card-text">
									Em caso de dúvidas sobre pagamentos ou datas, entre em contato
									com nossa Central de Atendimento.
								</p>
								<a href="/CentralDeAtendimento/central-atendimento" className="btn btn-outline-warning">
									Central de atendimento
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CalendarioPagamento;